import { useState, useEffect } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import { authService } from '../utils/auth.ts';
import { formatDate, formatFileSize } from '../utils/dateUtils';
import { Delete, ArrowCircleDown, Edit } from '@mui/icons-material';
import CommentSection from './CommentSection';
import ThumbnailEditModal from './ThumbnailEditModal';
import ImageViewer from './ImageViewer';
import '../styles/comments.css';

interface Photo {
  PK: string;
  filename: string;
  s3_key: string;
  uploader_id: string;
  uploader_username: string;
  uploaded_at: string;
  file_size: number;
}

interface PhotoDetailProps {
  photoId: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPhotoDeleted?: () => void;
  onThumbnailUpdated?: (photoId: string) => void;
}

const PhotoDetail: React.FC<PhotoDetailProps> = ({
  photoId,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onPhotoDeleted,
  onThumbnailUpdated,
}) => {
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbnailEdit, setShowThumbnailEdit] = useState(false);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    const loadPhotoDetail = async () => {
      try {
        setLoading(true);
        setError('');

        // 写真詳細情報取得
        const photoResponse = await authService.authFetch(`/photos/${photoId}`);
        if (!photoResponse.ok) {
          throw new Error('写真情報の取得に失敗しました');
        }
        const photoData = await photoResponse.json();
        setPhoto(photoData);

        // フルサイズ画像URL取得
        const urlResponse = await authService.authFetch(
          `/photos/${photoId}/url`
        );
        if (!urlResponse.ok) {
          throw new Error('画像URLの取得に失敗しました');
        }
        const urlData = await urlResponse.json();
        setImageUrl(urlData.url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'エラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    loadPhotoDetail();
  }, [photoId]);

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      } else if (event.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        onPrevious();
      } else if (event.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext, isFullscreen]);

  const handleDelete = async () => {
    if (!photo) return;

    const confirmed = window.confirm(
      `「${photo.filename}」を削除しますか？\n\nこの操作は取り消せません。`
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      const response = await authService.authFetch(`/photos/${photoId}`, {
        method: 'DELETE',
        invalidateCache: ['/api/photos', `/photos/${photoId}`], // 写真一覧と該当写真のキャッシュを無効化
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('この写真を削除する権限がありません');
        }
        throw new Error('写真の削除に失敗しました');
      }

      // 削除成功時の処理
      onPhotoDeleted?.();
    } catch (err) {
      alert(
        err instanceof Error ? err.message : '削除中にエラーが発生しました'
      );
    } finally {
      setDeleting(false);
    }
  };

  // 削除権限をチェック（自分の写真か管理者か）
  const canDelete = () => {
    if (!photo) return false;
    const currentUser = authService.getCurrentUser();
    return (
      currentUser?.role === 'admin' || photo.uploader_id === currentUser?.id
    );
  };

  // ダウンロード処理
  const handleDownload = async () => {
    if (!photo || !imageUrl) return;

    try {
      setDownloading(true);

      // 画像をfetchして blob として取得
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('画像の取得に失敗しました');
      }

      const blob = await response.blob();

      // ダウンロード用のリンクを作成
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photo.filename; // 元のファイル名を使用
      document.body.appendChild(link);
      link.click();

      // クリーンアップ
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        err instanceof Error
          ? err.message
          : 'ダウンロード中にエラーが発生しました'
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '0' : '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--neutral-50)',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          maxWidth: isMobile ? '100vw' : '90vw',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100%' : 'auto',
          height: isMobile ? '100%' : 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? '0' : 'auto',
          left: isMobile ? '0' : 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          style={{
            padding: isMobile ? '8px 12px' : '20px',
            borderBottom: '1px solid var(--neutral-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2
            style={{
              margin: 0,
              color: 'var(--neutral-800)',
              fontSize: isMobile ? '1rem' : '1.5rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              marginRight: '12px',
            }}
          >
            {photo?.filename || 'Loading...'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: isMobile ? '20px' : '24px',
              cursor: 'pointer',
              color: 'var(--neutral-600)',
              padding: '4px',
              minWidth: isMobile ? '32px' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* コンテンツ */}
        <div
          style={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          {/* 画像 */}
          <div
            style={{
              flex: isMobile ? '0 0 45vh' : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--neutral-100)',
              position: 'relative',
              minHeight: isMobile ? '45vh' : '400px',
            }}
          >
            {loading && (
              <div style={{ textAlign: 'center', color: 'var(--neutral-600)' }}>
                読み込み中...
              </div>
            )}

            {error && (
              <div style={{ textAlign: 'center', color: 'var(--error)' }}>
                {error}
              </div>
            )}

            {imageUrl && !loading && !error && (
              <img
                src={imageUrl}
                alt={photo?.filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
                onClick={() => setIsFullscreen(true)}
              />
            )}

            {/* ナビゲーションボタン */}
            {hasPrevious && onPrevious && (
              <button
                onClick={onPrevious}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
              >
                ‹
              </button>
            )}

            {hasNext && onNext && (
              <button
                onClick={onNext}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
              >
                ›
              </button>
            )}
          </div>

          {/* サイドバー情報 */}
          <div
            style={{
              width: isMobile ? 'auto' : '300px',
              padding: isMobile ? '8px 12px' : '12px',
              backgroundColor: 'var(--neutral-100)',
              borderLeft: isMobile ? 'none' : '1px solid var(--neutral-200)',
              borderTop: isMobile ? '1px solid var(--neutral-200)' : 'none',
              overflow: 'auto',
              flex: isMobile ? '1' : 'none',
            }}
          >
            {photo && (
              <>
                <div
                  style={{
                    marginBottom: isMobile ? '4px' : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: 'var(--neutral-800)',
                      fontSize: isMobile ? '0.8rem' : '1rem',
                      minWidth: isMobile ? '40px' : '60px',
                      flex: '0 0 auto',
                    }}
                  >
                    投稿者:
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--neutral-600)',
                      fontSize: isMobile ? '0.75rem' : '1rem',
                      flex: 1,
                    }}
                  >
                    {photo.uploader_username}
                  </p>
                </div>

                <div
                  style={{
                    marginBottom: isMobile ? '4px' : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: 'var(--neutral-800)',
                      fontSize: isMobile ? '0.8rem' : '1rem',
                      minWidth: isMobile ? '40px' : '60px',
                      flex: '0 0 auto',
                    }}
                  >
                    日時:
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--neutral-600)',
                      fontSize: isMobile ? '0.75rem' : '1rem',
                      flex: 1,
                    }}
                  >
                    {formatDate(photo.uploaded_at)}
                  </p>
                </div>

                <div
                  style={{
                    marginBottom: isMobile ? '4px' : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: 'var(--neutral-800)',
                      fontSize: isMobile ? '0.8rem' : '1rem',
                      minWidth: isMobile ? '40px' : '60px',
                      flex: '0 0 auto',
                    }}
                  >
                    サイズ:
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--neutral-600)',
                      fontSize: isMobile ? '0.75rem' : '1rem',
                      flex: 1,
                    }}
                  >
                    {formatFileSize(photo.file_size)}
                  </p>
                </div>

                <div
                  style={{
                    marginBottom: isMobile ? '4px' : '12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: isMobile ? '6px' : '8px',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: 'var(--neutral-800)',
                      fontSize: isMobile ? '0.8rem' : '1rem',
                      minWidth: isMobile ? '40px' : '60px',
                      flex: '0 0 auto',
                      paddingTop: '1px',
                    }}
                  >
                    ファイル:
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--neutral-600)',
                      wordBreak: 'break-all',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      flex: 1,
                      lineHeight: '1.4',
                    }}
                  >
                    {photo.filename}
                  </p>
                </div>

                {/* アクションボタン */}
                <div
                  style={{
                    marginTop: isMobile ? '6px' : '16px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* 削除ボタン（左、小さめ） */}
                  {canDelete() && (
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      style={{
                        backgroundColor: deleting
                          ? 'var(--neutral-400)'
                          : 'var(--error)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: isMobile ? '10px 12px' : '6px 12px',
                        cursor: deleting ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: isMobile ? '40px' : '50px',
                        flex: isMobile ? '0 0 auto' : '0 0 auto',
                        boxSizing: 'border-box',
                      }}
                      title="写真を削除"
                    >
                      {deleting ? '削除中' : <Delete />}
                    </button>
                  )}

                  {/* サムネイル編集ボタン */}
                  {canDelete() && (
                    <button
                      onClick={() => setShowThumbnailEdit(true)}
                      style={{
                        backgroundColor: 'var(--primary-600)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: isMobile ? '10px 12px' : '6px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: isMobile ? '40px' : '50px',
                        flex: isMobile ? '0 0 auto' : '0 0 auto',
                        boxSizing: 'border-box',
                      }}
                      title="サムネイルを編集"
                    >
                      <Edit />
                    </button>
                  )}

                  {/* ダウンロードボタン（右、大きめ・メイン） */}
                  <button
                    onClick={handleDownload}
                    disabled={downloading || !imageUrl}
                    style={{
                      backgroundColor: downloading
                        ? 'var(--neutral-400)'
                        : 'var(--success)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: isMobile ? '12px 16px' : '8px 16px',
                      cursor:
                        downloading || !imageUrl ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      flex: isMobile ? '1 1 auto' : '1 1 auto',
                      minWidth: '0',
                      maxWidth: isMobile ? 'auto' : '250px',
                      boxSizing: 'border-box',
                    }}
                    title="画像をダウンロード"
                  >
                    {downloading ? (
                      'ダウンロード中...'
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ArrowCircleDown />
                        ダウンロード
                      </div>
                    )}
                  </button>
                </div>

                {/* コメントセクション */}
                <CommentSection photoId={photoId} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* 高機能画像ビューアー */}
      {isFullscreen && imageUrl && (
        <ImageViewer
          imageUrl={imageUrl}
          alt={photo?.filename || 'Photo'}
          onClose={() => setIsFullscreen(false)}
          onPrevious={hasPrevious && onPrevious ? onPrevious : undefined}
          onNext={hasNext && onNext ? onNext : undefined}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      )}

      {/* サムネイル編集モーダル */}
      {showThumbnailEdit && photo && (
        <ThumbnailEditModal
          photo={photo}
          onClose={() => setShowThumbnailEdit(false)}
          onThumbnailUpdated={() => {
            // サムネイル更新後の処理
            setShowThumbnailEdit(false);
            // 個別サムネイル更新通知
            onThumbnailUpdated?.(photoId);
          }}
        />
      )}
    </div>
  );
};

export default PhotoDetail;
